import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, Collapse, Divider, Table, Typography } from 'antd';
import axios from '../../utils/axios';

const { Title, Paragraph, Text } = Typography;

const SCOPES = [
  { scope: 'mcp:read', description: 'Baseline read access across every enabled MCP tool group. Included on every token.' },
  { scope: 'mcp:config:write', description: 'Write access to config-like resources (lifecycles, canonical properties). Not currently issuable.' },
  { scope: 'mcp:integrations:read', description: 'Read access to integration metadata and statistics. Not currently issuable.' },
];

const SCOPE_COLUMNS = [
  { title: 'Scope', dataIndex: 'scope', key: 'scope', render: (v) => <Text code>{v}</Text> },
  { title: 'Grants', dataIndex: 'description', key: 'description' },
];

function mcpConfigSnippet(mcpServerUrl) {
  return JSON.stringify(
    { mcpServers: { touchflow: { url: mcpServerUrl, headers: { Authorization: 'Bearer tfmcp_...' } } } },
    null,
    2,
  );
}

export default function McpSetupDocsPage() {
  const [mcpServerUrl, setMcpServerUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get('/ai/prompts')
      .then((response) => {
        if (!cancelled) setMcpServerUrl(response.data?.mcp_server_url || '');
      })
      .catch(() => {
        // Leave the URL blank rather than showing a broken page — the
        // snippet below just omits it until the endpoint is reachable.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <Title level={2}>Connect an AI agent</Title>
      <Paragraph type="secondary">
        TouchFlow can expose the analysis data to external AI agents (Cursor,
        Claude Code, Claude Desktop) through the Model Context Protocol (MCP), scoped to whichever
        tool groups your tenant has enabled. The aggregated MRR figures are anonymised before 
        release (k-anonymity + noise).
      </Paragraph>

      <Card title="Connect with OAuth (recommended)" style={{ marginBottom: 24 }}>
        <Paragraph>
          Add TouchFlow as an MCP server in your client (Cursor, Claude Code, or Claude Desktop)
          using the URL below, then authenticate. Supported clients use OAuth, so you'll be
          prompted to sign in and approve access — no token to create, copy, or rotate. Once
          connected, the client shows up under "Connected Apps" on the{' '}
          <Link to="/mcp-tokens">MCP API Tokens</Link> page, where you can also revoke access.
        </Paragraph>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>MCP server URL</Text>
        {mcpServerUrl && (
          <Paragraph copyable={{ text: mcpServerUrl }} code style={{ fontSize: 16, marginBottom: 0 }}>
            {mcpServerUrl}
          </Paragraph>
        )}
      </Card> 

      <Card title="Scopes" style={{ marginBottom: 24 }}>
        <Table
          columns={SCOPE_COLUMNS}
          dataSource={SCOPES}
          rowKey="scope"
          pagination={false}
          size="small"
        />
      </Card>

      <Divider />

      <Card
        title={<Text type="secondary">Manual tokens (legacy — headless or CI clients only)</Text>}
        style={{ marginBottom: 24 }}
        bordered
      >
        <Collapse defaultActiveKey={[]} ghost style={{ background: 'none' }}>
          <Collapse.Panel
            header={<span style={{ color: 'var(--ant-primary-color)' }}>Show manual token instructions</span>}
            key="manual"
          >
            <Alert
              type="info"
              showIcon
              message="Managing tokens and connected apps"
              description={
                <>
                  Manual token creation, connected-app management, and session revocation live on the{' '}
                  <Link to="/mcp-tokens">MCP API Tokens</Link> page. That page is only visible to
                  tenant admins — ask an admin on your team if you don't see it in navigation.
                </>
              }
            />
            <Paragraph type="secondary">
              Manual API tokens exist only for clients that can't do the OAuth flow (headless
              environments, CI pipelines). Prefer OAuth above whenever your client supports it —
              tokens must be created, stored, and rotated by hand, and are wider-scoped for longer.
            </Paragraph>
            <Paragraph type="secondary">
              If you do need one, create it from the <Link to="/mcp-tokens">MCP API Tokens</Link> page
              (tenant-admin role required) and use a config snippet like this, substituting your
              token:
            </Paragraph>
            <Paragraph copyable={{ text: mcpConfigSnippet(mcpServerUrl || '<mcp_server_url>') }} code style={{ whiteSpace: 'pre-wrap' }}>
              {mcpConfigSnippet(mcpServerUrl || '<mcp_server_url>')}
            </Paragraph>
          </Collapse.Panel>
        </Collapse>
      </Card>
    </div>
  );
}
